import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaIdBadge, FaSearch, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Parse from 'parse';

// Services
import { agencyService } from '../../services/agencyService';
import { authService } from '../../services/ParseService';

// Models
import Host from '../../models/Host';
import AgencyInvitation from '../../models/AgencyInvitation';
import SystemMessage from '../../models/SystemMessage';
import User from '../../models/User';
import Agent from '../../models/Agent';

// Styled Components
const LoadingDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 250px;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2rem;
`;

const Section = styled.div`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CodeForm = styled.form`
  display: flex;
  gap: 1rem;
`;

const CodeInput = styled.input`
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #1a2a6c;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #2a3a7c;
  }
`;

const Divider = styled.div`
  text-align: center;
  margin: 1rem 0;
  color: #666;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 45%;
    height: 1px;
    background: #ddd;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  display: flex;
  gap: 1rem;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
`;

const SearchButton = styled(SubmitButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AgencyCard = styled.div`
  border: 1px solid #ebebeb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.12);
  }

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }

  .content {
    padding: 20px;

    h3 {
      margin: 0 0 10px;
      font-size: 18px;
      color: #222;
    }

    p {
      margin: 0 0 15px;
      color: #717171;
      font-size: 14px;
      line-height: 1.4;
    }

    .stats {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #717171;
      font-size: 14px;

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const AgenciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  padding: 0 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const AgencyDetailsContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const AgencyDetailsHeader = styled.div`
  background: #fff;
  padding: 1rem;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const AgencyDetailsContent = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 80%;
  max-width: 900px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
`;

const StatSection = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 1rem;
  background: #f9f9f9;
  border-bottom: 1px solid #eee;
`;

const StatItem = styled.div`
  text-align: center;

  span {
    font-weight: bold;
    color: #333;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: #f0f0f0;
  border-bottom: 1px solid #ddd;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  cursor: pointer;
  color: #555;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #e0e0e0;
  }

  &.active {
    background-color: #ddd;
    color: #333;
    font-weight: bold;
  }
`;

const TabContent = styled.div`
  padding: 2rem;
`;

const JoinButton = styled(SubmitButton)`
  width: calc(100% - 4rem);
  margin: 2rem;
  padding: 1rem;
  border-radius: 8px;
`;

const NoAgenciesMessage = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  color: #666;
  padding: 20px;
`;

const AgencyInfo = styled.div`
  h3 {
    margin: 0 0 0.5rem 0;
    color: #1a2a6c;
  }

  p {
    color: #666;
    margin-bottom: 1rem;
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  backdrop-filter: blur(5px);
`;

const ConfirmationContent = styled.div`
  background: #1a1a1a;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.25);
  position: relative;
  z-index: 1200;
  color: #ffffff;

  h3 {
    color: #ffffff !important;
    margin-bottom: 1.5rem;
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin-bottom: 2rem;

    li {
      padding: 0.75rem;
      margin-bottom: 0.5rem;
      background: #2a2a2a;
      border-radius: 4px;
      color: #ffffff;
      font-size: 0.9rem;

      &:before {
        content: '\2022';
        color: #ffffff;
        font-weight: bold;
        margin-right: 8px;
      }
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #666;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }
`;

const ConfirmButton = styled(SubmitButton)`
  padding: 0.75rem 1.5rem;
`;

const JoinAgencyPage = () => {
  const navigate = useNavigate();
  const [agencyCode, setAgencyCode] = useState('');
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);
  const [activeTab, setActiveTab] = useState('description');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Debounced search function
  const debouncedSearchAgencies = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        await loadInitialAgencies();
        return;
      }

      try {
        setLoading(true);
        const fetchedAgencies = await agencyService.searchAgencies(query.trim());

        if (mountedRef.current) {
          setAgencies(fetchedAgencies);

          // If we found exactly one result and it matches a numeric code, show it directly
          const numericCode = Number(query.trim());
          if (fetchedAgencies.length === 1 && !isNaN(numericCode) && fetchedAgencies[0].uid === numericCode) {
            //setSelectedAgency(fetchedAgencies[0]);
            setActiveTab('description');
          }

          if (fetchedAgencies.length === 0) {
            toast.info('No agencies found matching your search');
          }
        }
      } catch (error) {
        console.error('Error searching agencies:', error);
        if (mountedRef.current) {
          toast.error(t('agency.join.searchError'));
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, 500),
    []
  );

  const { t } = useTranslation();

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearchAgencies(query);
  }, [debouncedSearchAgencies]);

  const handleSearchAgencies = useCallback(async () => {
    if (!searchQuery.trim()) {
      toast.error(t('agency.join.enterSearchQuery'));
      return;
    }

    debouncedSearchAgencies.flush();
  }, [searchQuery, debouncedSearchAgencies]);

  const loadInitialAgencies = useCallback(async () => {
    try {
      if (!mountedRef.current) return;
      setLoading(true);
      const preloadedAgencies = await agencyService.getPreloadedAgencies();

      if (mountedRef.current) {
        setAgencies(preloadedAgencies);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading initial agencies:', error);
      if (mountedRef.current) {
        toast.error(t('agency.join.loading.error'));
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadInitialAgencies();

    return () => {
      mountedRef.current = false;
    };
  }, [loadInitialAgencies]);

  const agenciesList = useMemo(() => {
    return agencies.map((agency, index) => (
      <AgencyCard
        key={agency.id || `agency-${index}`}
        onClick={() => setSelectedAgency(agency)}
      >
        <img
          src={agency.logo}
          alt={agency.name}
          onError={(e) => {
            e.target.src = 'default-agency-logo.png';
          }}
        />
        <div className="content">
          <h3>{agency.name}</h3>
          <p>
            <FaUsers style={{ marginRight: '5px' }} />
            {t('agency.join.details.members', { count: agency.memberCount || 0 })}
          </p>
          <p className="stats">
            <FaSearch style={{ marginRight: '3px' }} />
            <span>{agency.uid}</span>
          </p>
          <p>{agency.description || t('agency.join.details.noDescription')}</p>
        </div>
      </AgencyCard>
    ));
  }, [agencies]);

  const handleJoinRequest = useCallback(async (agency) => {
    try {

      setShowConfirmation(false); // Close the confirmation modal
      setLoading(true); // Show loading state
      
      // Fetch full agency details with author included
      const query = new Parse.Query(Agent);
      query.equalTo(Agent.keys.UID, agency.uid);
      query.include(Agent.keys.AUTHOR);
      const agent = await query.first();
      
      if (!agent) {
        throw new Error('Agency not found');
      }

      await checkHost(agent);
    } catch (error) {
      console.error('Error joining agency:', error);
      toast.error(t('agency.join.error.joinFailed'));
      setLoading(false);
    }
  }, [t]);

  const checkHost = async (agent) => {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) throw new Error('No user found');

      // Check if user already has a host
      if (currentUser.get(User.keys.HOST)) {
        await joinAgency(currentUser.get(User.keys.HOST), agent);
        return;
      }

      // Look for existing host
      const query = new Parse.Query(Host);
      query.equalTo(Host.keys.AUTHOR_ID, currentUser.id);
      const existingHost = await query.first();

      if (existingHost) {
        await joinAgency(existingHost, agent);
      } else {
        await createNewHost(agent);
      }
    } catch (error) {
      if (error.code === Parse.Error.OBJECT_NOT_FOUND) {
        await createNewHost(agent);
      } else {
        throw error;
      }
    }
  };

  const createNewHost = async (agent) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) throw new Error('No user found');

    const host = new Host();
    host.setAuthor(currentUser);
    host.setAuthorId = currentUser.id;

    const invitedByUser = currentUser.get('invitedByUser');
    if (invitedByUser) {
      host.setInviterId = invitedByUser;
    }

    const savedHost = await host.save();
    await updateUserHost(savedHost, agent);
  };

  const updateUserHost = async (host, agent) => {
    const query = new Parse.Query(Host);
    query.equalTo(Host.keys.OBJECT_ID, host.id);
    const newHost = await query.first();
    if (!newHost) throw new Error('Host not found');

    const currentUser = authService.getCurrentUser();
    if (!currentUser) throw new Error('No user found');

    currentUser.set(User.keys.HOST, newHost);
    currentUser.set(User.keys.HOST_ID, newHost.id);

    await currentUser.save();
    const updatedUser = await authService.refreshUser(['host']);
    
    if (updatedUser) {
      await joinAgency(newHost, agent);
    } else {
      throw new Error('Failed to update user');
    }
  };

  const joinAgency = async (host, agent) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) throw new Error('No user found');

    // Update user properties
    currentUser.setRole(User.roles.HOST);
    currentUser.set(User.keys.AGENCY, agent);
    currentUser.set(User.keys.AGENCY_ID, agent.id);
    currentUser.set(User.keys.LAST_AGENCY_DATE, new Date());

    const updatedUser = await currentUser.save();

    // Add user to Parse Role, this is done via cloud code now
    //await saveHostRole();

    if (updatedUser) {
      await becomeAgencyMember(host, agent);
    } else {
      throw new Error('Failed to update user');
    }
  };

  const saveHostRole = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) throw new Error('No user found');

    const hostRole = await new Parse.Query(Parse.Role).equalTo('name', User.roles.HOST).first();
    if (!hostRole) throw new Error('Host role not found');
    
    const roleUsers = hostRole.getUsers();
    roleUsers.add(currentUser);
    await hostRole.save();
  };  

  const becomeAgencyMember = async (host, agent) => {
    agent.addUnique(Agent.keys.HOST_IDS, host.id);
    await agent.save();
    await registerInvitation(host, agent);
  };

  const registerInvitation = async (host, agent) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) throw new Error('No user found');

    const invitation = new AgencyInvitation();
    invitation.setAgent = agent;
    invitation.setAgentId = agent.id;
    invitation.setHost = host;
    invitation.setHostId = host.id;
    invitation.setHostAuthor = currentUser;
    invitation.setHostAuthorId = currentUser.id;
    invitation.setInvitationType = AgencyInvitation.invitationType.JOIN;
    invitation.setInvitationStatus = AgencyInvitation.invitationStatus.ACCEPTED;

    const savedInvitation = await invitation.save();
    await updateHost(host, agent, savedInvitation);
  };

  const updateHost = async (host, agent, invitation) => {
    host.set(Host.keys.AGENCY, agent);
    await host.save();
    await sendSystemMessage(host, agent, invitation);
  };

  const sendSystemMessage = async (host, agent, invitation) => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) throw new Error('No user found');

    const systemMessage = new SystemMessage();
    systemMessage.setMessageType = SystemMessage.systemMessageType.HOST_JOIN_AGENCY;
    systemMessage.setAuthor = currentUser;
    systemMessage.setAgent = agent;
    systemMessage.setReceiver = agent.get('author');
    systemMessage.setHost = host;
    systemMessage.setMessageStatus = SystemMessage.systemMessageStatus.ACCEPTED;
    systemMessage.setInvitation = invitation;
    systemMessage.setRead = false;

    await systemMessage.save();

    toast.success(t('agency.join.success', { agencyName: agent.name }));
    setSelectedAgency(null); // Close the modal after sending the request
    setLoading(false);
    setTimeout(() => window.location.reload(), 2000);
  };

  const handleCodeSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!agencyCode.trim()) {
      toast.error(t('agency.join.enterSearchQuery'));
      return;
    }

    try {
      setLoading(true);
      // Search for agency using the code
      const agency = await agencyService.getAgencyByCode(agencyCode.trim());
      
      if (agency) {
        setSelectedAgency(agency);
        setActiveTab('description');
      } else {
        toast.error(t('agency.join.error.agencyNotFound'));
      }
    } catch (error) {
      console.error('Error finding agency by code:', error);
      if (error.message === 'Agency code must be a valid number') {
        toast.error(t('agency.join.error.invalidCode'));
      } else {
        toast.error(t('agency.join.error.searchFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [agencyCode]);

  // Render loading overlay
  const loadingOverlay = loading && (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }}
      />
      <LoadingDialog>
        <LoadingSpinner />
        <p style={{ marginTop: '1rem', color: '#333' }}>{t('agency.join.loading.message')}</p>
      </LoadingDialog>
    </>
  );

  return (
    <Container>
      {loadingOverlay}
      <Section>
        <h2>{t('agency.join.enterCode')}</h2>
        <CodeForm onSubmit={handleCodeSubmit}>
          <CodeInput 
            type="text"
            placeholder={t('agency.join.enterCode')}
            value={agencyCode}
            onChange={(e) => setAgencyCode(e.target.value)}
          />
          <SubmitButton type="submit">{t('agency.join.joinByCode')}</SubmitButton>
        </CodeForm>
      </Section>

      <Divider>{t('agency.join.or')}</Divider>

      <SearchContainer>
        <SearchInput 
          type="text"
          placeholder={t('agency.join.searchPlaceholder')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <SearchButton 
          onClick={handleSearchAgencies} 
          disabled={loading}
        >
          <FaSearch style={{ marginRight: '5px' }} /> 
          {loading ? t('agency.join.searching') : t('agency.join.searchButton')}
        </SearchButton>
      </SearchContainer>

      <AgenciesGrid>
        {agencies.length === 0 ? (
          <NoAgenciesMessage>
            {t('agency.join.noAgenciesFound')}
          </NoAgenciesMessage>
        ) : (
          agenciesList
        )}
      </AgenciesGrid>

      {selectedAgency && (
        <AgencyDetailsContainer>
          <AgencyDetailsContent>
            <AgencyDetailsHeader>
              <h2>{selectedAgency.name} <span>(ID: {selectedAgency.uid})</span></h2>
              <CloseButton onClick={() => setSelectedAgency(null)}>{t('agency.join.close')}</CloseButton>
            </AgencyDetailsHeader>

            <TabContainer>
              <TabButton
                className={activeTab === 'description' ? 'active' : ''}
                onClick={() => setActiveTab('description')}
              >
                {t('agency.join.details.tabs.description')}
              </TabButton>
              <TabButton
                className={activeTab === 'rules' ? 'active' : ''}
                onClick={() => setActiveTab('rules')}
              >
                {t('agency.join.details.tabs.rules')}
              </TabButton>
              <TabButton
                className={activeTab === 'commissions' ? 'active' : ''}
                onClick={() => setActiveTab('commissions')}
              >
                {t('agency.join.details.tabs.commissions')}
              </TabButton>
              <TabButton
                className={activeTab === 'support' ? 'active' : ''}
                onClick={() => setActiveTab('support')}
              >
                {t('agency.join.details.tabs.support')}
              </TabButton>
            </TabContainer>

            <TabContent>
              {activeTab === 'description' && (
                <>
                  <img
                    src={selectedAgency.logo}
                    alt={selectedAgency.name}
                    style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem' }}
                  />
                  <p>{selectedAgency.description || t('agency.join.details.noDescription')}</p>
                  <StatSection>
                    <StatItem>
                      <FaUsers />
                      <span> {t('agency.join.details.members', { count: selectedAgency.memberCount || 0 })}</span>
                    </StatItem>
                  </StatSection>
                </>
              )}
              {activeTab === 'rules' && (
                <p>{selectedAgency.rules || t('agency.join.details.noRules')}</p>
              )}
              {activeTab === 'commissions' && (
                <p>{selectedAgency.commissions || t('agency.join.details.noCommissions')}</p>
              )}
              {activeTab === 'support' && (
                <p>{selectedAgency.support || t('agency.join.details.noSupport')}</p>
              )}
            </TabContent>

            <JoinButton onClick={() => setShowConfirmation(true)}>{t('agency.join.requestToJoin')}</JoinButton>

            {showConfirmation && (
              <ConfirmationModal style={{ color: '#ffffff' }}>
                <ConfirmationContent>
                  <h3>{t('agency.join.confirmJoin.title')}</h3>
                  <ul>
                    <li>{t('agency.join.confirmJoin.notes.leaveReason')}</li>
                    <li>{t('agency.join.confirmJoin.notes.multipleAgents')}</li>
                    <li>{t('agency.join.confirmJoin.notes.otherAgents')}</li>
                  </ul>
                  <ButtonGroup>
                    <CancelButton onClick={() => setShowConfirmation(false)}>{t('agency.join.confirmJoin.cancel')}</CancelButton>
                    <ConfirmButton onClick={() => handleJoinRequest(selectedAgency)}>{t('agency.join.confirmJoin.confirm')}</ConfirmButton>
                  </ButtonGroup>
                </ConfirmationContent>
              </ConfirmationModal>
            )}
          </AgencyDetailsContent>
        </AgencyDetailsContainer>
      )}
    </Container>
  );
};

export default React.memo(JoinAgencyPage);